import { useState, useEffect, useRef } from 'react';

// Dice icons for each type
const DICE_ICONS: Record<number, string> = {
  4: '▲',
  6: '⬡',
  8: '◆',
  10: '⬟',
  12: '⬢',
  20: '⬣',
  100: '%',
};

export type AnimationPhase = 'idle' | 'launch' | 'cycling' | 'landing' | 'result';

interface AnimatedDieProps {
  sides: number;
  result: number | null;
  phase: AnimationPhase;
  delay?: number;
}

export function AnimatedDie({ sides, result, phase, delay = 0 }: AnimatedDieProps) {
  const [displayNumber, setDisplayNumber] = useState(1);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const icon = DICE_ICONS[sides] || '?';

  // Number cycling during 'cycling' phase
  useEffect(() => {
    if (phase !== 'cycling') {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      return;
    }

    let speed = 40;
    const tick = () => {
      const maxValue = sides === 100 ? 100 : sides;
      setDisplayNumber(Math.floor(Math.random() * maxValue) + 1);
      speed = Math.min(speed + 10, 100);
      timeoutRef.current = setTimeout(tick, speed);
    };

    // Start after delay
    const startTimeout = setTimeout(tick, delay);

    return () => {
      clearTimeout(startTimeout);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [phase, sides, delay]);

  // Set final result when landing
  useEffect(() => {
    if ((phase === 'landing' || phase === 'result') && result !== null) {
      setDisplayNumber(result);
    }
  }, [phase, result]);

  const isMaxRoll = result !== null && result === sides;
  const isMinRoll = result !== null && result === 1;

  // Build container classes based on phase
  const getContainerClasses = () => {
    const base = `relative w-14 h-14 flex items-center justify-center
      bg-background-card border rounded-lg font-mono font-bold text-lg
      will-change-transform transition-all`;

    switch (phase) {
      case 'launch':
        return `${base} animate-dice-launch border-accent-bronze/30`;
      case 'cycling':
        return `${base} border-accent-bronze/30`;
      case 'landing':
        return `${base} animate-dice-land border-accent-gold/50`;
      case 'result':
        if (isMaxRoll) {
          return `${base} animate-glow-pulse border-accent-gold bg-accent-gold/20 text-accent-gold`;
        }
        if (isMinRoll) {
          return `${base} border-error bg-error/20 text-error`;
        }
        return `${base} border-background-elevated text-text-primary`;
      default:
        return `${base} border-background-elevated text-text-primary`;
    }
  };

  // Show die icon in background during launch/cycling
  const showBackgroundIcon = phase === 'launch' || phase === 'cycling';

  // Show number during cycling/landing/result
  const showNumber = phase === 'cycling' || phase === 'landing' || phase === 'result';

  return (
    <div
      className={getContainerClasses()}
      style={{
        animationDelay: `${delay}ms`,
      }}
      data-testid="die-container"
    >
      {/* Die icon in background */}
      {showBackgroundIcon && (
        <span
          className="absolute text-3xl text-accent-bronze/40 select-none"
          style={{
            filter: phase === 'cycling' ? 'blur(2px)' : undefined,
          }}
        >
          {icon}
        </span>
      )}

      {/* Number display */}
      {showNumber && (
        <span
          className={`relative z-10 ${phase === 'cycling' ? 'text-text-primary' : ''}`}
          data-testid="die-number"
        >
          {displayNumber}
        </span>
      )}

      {/* Idle state shows just the icon */}
      {phase === 'idle' && (
        <span className="text-2xl text-text-secondary">{icon}</span>
      )}
    </div>
  );
}
